/**
 * iter133 Phase 2 — Public referral landing page.
 *
 * Route: /ref/:code
 * Behavior:
 *   - Calls GET /api/ref/{code} which validates the referral code, logs
 *     the link_opened event, and returns the referrer's display name.
 *   - Stores `referred_by_code` in localStorage so it's carried through
 *     the estimate/booking/signup flow and attributed on the backend.
 *   - Redirects to /estimate after a brief welcome moment.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RefLandingPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, referrer: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ref/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (data?.valid) {
          // Persist attribution across estimate/signup/booking
          try { localStorage.setItem("referred_by_code", data.referrer_code || code); }
          catch { /* localStorage may be disabled — non-fatal */ }
          setState({ loading: false, valid: true, referrer: data.referrer_name || "A neighbor" });
          setTimeout(() => navigate("/estimate", { replace: true }), 2400);
        } else {
          setState({ loading: false, valid: false, referrer: "" });
          setTimeout(() => navigate("/estimate", { replace: true }), 2000);
        }
      } catch {
        if (!cancelled) {
          setState({ loading: false, valid: false, referrer: "" });
          setTimeout(() => navigate("/estimate", { replace: true }), 2000);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [code, navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-[#062818] via-[#0b3a24] to-[#047857] px-4"
         data-testid="ref-landing-page">
      <div className="w-full max-w-md bg-white/95 backdrop-blur rounded-3xl p-8 text-center shadow-2xl">
        <div className="mx-auto mb-4 grid place-items-center w-14 h-14 rounded-full bg-emerald-50">
          {state.loading
            ? <Loader2 className="w-6 h-6 animate-spin text-[#047857]" />
            : <Sparkles className="w-6 h-6 text-[#047857]" />}
        </div>
        {state.loading ? (
          <>
            <div className="text-lg font-extrabold text-[#062818] font-[Outfit]">Loading your invitation…</div>
            <div className="text-sm text-gray-500 mt-1">Code: <span className="font-mono">{code}</span></div>
          </>
        ) : state.valid ? (
          <>
            <div className="text-xs uppercase tracking-widest text-[#047857] font-black mb-1">You&apos;ve been referred</div>
            <h1 className="text-2xl font-extrabold text-[#062818] font-[Outfit] leading-tight">
              {state.referrer} recommended <br /> JDSN Lawn Care to you
            </h1>
            <p className="text-sm text-gray-600 mt-3">
              Take 30 seconds — get your instant lawn-mowing quote below. You&apos;ll help
              your neighbor earn a service credit.
            </p>
            <div className="mt-4 text-xs text-gray-400">Redirecting to your quote…</div>
          </>
        ) : (
          <>
            <div className="text-lg font-extrabold text-[#062818] font-[Outfit]">Welcome to JDSN Lawn Care</div>
            <div className="text-sm text-gray-500 mt-1">This referral link is not recognized, but you can still get an instant quote — one moment…</div>
          </>
        )}
      </div>
    </div>
  );
}
